// File: src/components/OmniCine/Timeline/Transport/TransportBar.tsx
//
// TransportBar — OmniCine Timeline A3 (contract-safe)
// - Deterministic play runner (~30fps) with clamp (ref-driven; avoids stale closure)
// - Prev/Next marker navigation
// - Marker authoring (normal + npc:hit)
// - Snap toggle, zoom controls (canonical viewport.pxPerSec; safe fallback)
// - Loop selection (arms selected selection range; falls back to last selection)
// - Phase authoring: Add Phase Ranges (EMITS event → TimelineShell owns add/repair logic)
// - AI + Export events preserved
// - ✅ Help button emits BOTH legacy + current events
// - ✅ Dock pills: uniform sizing + spacing (Import/Export/Help/etc. no longer squeezed)
// - ✅ Semantic color variants use RGBA arbitrary values (works even if Tailwind color/opacity is unsupported)
//

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useOmniCineTimeline } from "@/state/useOmniCineTimeline";
import { useOptionalOmniStudioGraph } from "@/state/useOmniStudioGraph";
import { snapTime } from "@/lib/omnicine/snapTime";
import {
  buildNpcEnvelopeSuggestions,
  emitNpcEnvelopeSuggestions,
} from "@/lib/omnicine/npcEnvelopes";
import PodcastExportPanel from "@/bridge/omnicine/export/PodcastExportPanel";
import ImportButton from "@/components/OmniCine/Toolbar/ImportButton";
import {
  emitAddPhaseRanges,
  emitMarkerAdded,
  emitTimelineHelpOpen,
  emitTransportPlaying,
} from "@/contracts/omnicine/TimelineSurfaceEvents";

/* ------------------------------------------------------------------ */
/* Utils                                                              */
/* ------------------------------------------------------------------ */

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

function formatTime(sec: number) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function finiteNum(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

function markerFrameIndex(timeSec: number, fps: number): number {
  return Math.max(0, Math.round(timeSec * Math.max(1, fps)));
}

const MANUAL_MARKER_DEFAULT_COLOR = "#38bdf8";
const NPC_MARKER_DEFAULT_COLOR = "#34d399";

type SnapMode = "frame" | "second" | "marker" | "beat";

function cycleSnapMode(mode: SnapMode): SnapMode {
  switch (mode) {
    case "frame":
      return "second";
    case "second":
      return "marker";
    case "marker":
      return "beat";
    default:
      return "frame";
  }
}

function formatSnapModeLabel(mode: SnapMode): string {
  switch (mode) {
    case "frame":
      return "Frame";
    case "second":
      return "Second";
    case "marker":
      return "Marker";
    case "beat":
      return "Beat";
    default:
      return "Frame";
  }
}

function makeId(): string {
  try {
    const c = globalThis.crypto as Crypto | undefined;
    if (c?.randomUUID) return c.randomUUID();
  } catch {
    // ignore
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** Canonical pxPerSec reader (viewport-first; safe fallback). */
function readPxPerSec(state: any) {
  const viewport = state?.viewport ?? null;

  const px = finiteNum(viewport?.pxPerSec)
    ? Number(viewport.pxPerSec)
    : finiteNum(state?.pxPerSec)
      ? Number(state.pxPerSec)
      : 100;

  return clamp(px, 20, 400);
}

/* ------------------------------------------------------------------ */
/* Dock pill styling (geometry first, color variants second)          */
/* ------------------------------------------------------------------ */

/**
 * IMPORTANT:
 * DOCK_BTN_BASE is geometry-only.
 * Color variants apply bg/border/hover/ring so we never "lose" color to a neutral base.
 */
const DOCK_BTN_BASE =
  "h-9 min-w-[96px] px-3 rounded-xl text-[12px] leading-none whitespace-nowrap " +
  "inline-flex items-center justify-center gap-2 " +
  "border shadow-sm transition-colors select-none";

const DOCK_BTN_DISABLED =
  "cursor-not-allowed opacity-50 hover:opacity-50";

/** Neutral (glass) */
const V_NEUTRAL =
  "text-white/90 border-white/12 " +
  "bg-[rgba(255,255,255,0.07)] hover:bg-[rgba(255,255,255,0.11)]";

/** “Authoring / timeline ops” (cyan) */
const V_AUTHOR =
  "text-white/95 border-[rgba(34,211,238,0.35)] " +
  "bg-[rgba(34,211,238,0.16)] hover:bg-[rgba(34,211,238,0.22)] " +
  "ring-1 ring-[rgba(34,211,238,0.18)]";

/** “NPC / intelligence” (violet) */
const V_NPC =
  "text-white/95 border-[rgba(167,139,250,0.38)] " +
  "bg-[rgba(167,139,250,0.16)] hover:bg-[rgba(167,139,250,0.22)] " +
  "ring-1 ring-[rgba(167,139,250,0.18)]";

/** “State ON / snap” (amber) */
const V_TOGGLE_ON =
  "text-white/95 border-[rgba(252,211,77,0.40)] " +
  "bg-[rgba(252,211,77,0.18)] hover:bg-[rgba(252,211,77,0.24)] " +
  "ring-1 ring-[rgba(252,211,77,0.18)]";

/** “Export / IO” (emerald) */
const V_EXPORT =
  "text-white/95 border-[rgba(110,231,183,0.38)] " +
  "bg-[rgba(110,231,183,0.16)] hover:bg-[rgba(110,231,183,0.22)] " +
  "ring-1 ring-[rgba(110,231,183,0.16)]";

/** Help is slightly calmer */
const V_HELP =
  "text-white/85 border-white/12 " +
  "bg-[rgba(255,255,255,0.06)] hover:bg-[rgba(255,255,255,0.10)]";

function StatusDot({
  on,
  tone,
}: {
  on: boolean;
  tone: "amber" | "violet" | "emerald" | "cyan";
}) {
  if (!on) return null;

  const toneBg =
    tone === "amber"
      ? "bg-[rgba(252,211,77,0.95)]"
      : tone === "violet"
        ? "bg-[rgba(167,139,250,0.95)]"
        : tone === "emerald"
          ? "bg-[rgba(110,231,183,0.95)]"
          : "bg-[rgba(34,211,238,0.95)]";

  return (
    <span
      className={cx(
        "inline-block h-2.5 w-2.5 rounded-full",
        toneBg,
        "shadow-sm shadow-black/30 ring-1 ring-white/15",
      )}
      aria-hidden
    />
  );
}

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

export default function TransportBar(): JSX.Element {
  const { state, dispatch } = useOmniCineTimeline();
  const omniStudioGraph = useOptionalOmniStudioGraph();
  const embeddedInOmniStudio = omniStudioGraph != null;
  const lastImportedLaneFocusRef = useRef<{
    mode: "import";
    visibleTrackIds: string[];
    anchorTrackId: string | null;
    hideAuxiliaryLanes: boolean;
    sourceClipId: string | null;
  } | null>(null);

  // ✅ A3-safe snap fallback
  const snap = state.snap ?? { enabled: false, mode: "second", fps: 30, bpm: 120, beatDiv: 1 };

  /* -------------------------------------------------------------- */
  /* Canonical viewport pxPerSec (derived, display only)             */
  /* -------------------------------------------------------------- */

  const pxPerSec = useMemo(() => readPxPerSec(state as any), [state]);

  /* ----------------------------------------------------- */
  /* Local UI toggles                                      */
  /* ----------------------------------------------------- */

  const [aiEnabled, setAiEnabled] = useState(false);

  useEffect(() => {
    const laneFocus = (state as any)?.laneFocus ?? null;
    if (laneFocus?.mode !== "import") return;

    const visibleTrackIds = Array.isArray(laneFocus?.visibleTrackIds)
      ? laneFocus.visibleTrackIds.map((trackId: unknown) => String(trackId ?? "").trim()).filter(Boolean)
      : [];
    if (visibleTrackIds.length === 0) return;

    lastImportedLaneFocusRef.current = {
      mode: "import",
      visibleTrackIds,
      anchorTrackId: String(laneFocus?.anchorTrackId ?? "").trim() || null,
      hideAuxiliaryLanes: !!laneFocus?.hideAuxiliaryLanes,
      sourceClipId: String(laneFocus?.sourceClipId ?? "").trim() || null,
    };
  }, [state]);

  /* ----------------------------------------------------- */
  /* Marker helpers (prev/next)                            */
  /* ----------------------------------------------------- */

  const markersSorted = useMemo(() => {
    const xs = (state.markers ?? [])
      .map((m) => m?.timeSec)
      .filter((t) => finiteNum(t)) as number[];
    xs.sort((a, b) => a - b);
    return xs;
  }, [state.markers]);

  const prevMarker = useMemo(() => {
    const p = finiteNum(state.playheadSec) ? state.playheadSec : 0;
    for (let i = markersSorted.length - 1; i >= 0; i--) {
      if (markersSorted[i] < p - 1e-6) return markersSorted[i];
    }
    return null;
  }, [markersSorted, state.playheadSec]);

  const nextMarker = useMemo(() => {
    const p = finiteNum(state.playheadSec) ? state.playheadSec : 0;
    for (let i = 0; i < markersSorted.length; i++) {
      if (markersSorted[i] > p + 1e-6) return markersSorted[i];
    }
    return null;
  }, [markersSorted, state.playheadSec]);

  /* ----------------------------------------------------- */
  /* Loop selection arming                                 */
  /* ----------------------------------------------------- */

  const armedSelection = useMemo(() => {
    const id = (state as any).selectedRangeId as string | null;
    if (!id) return null;

    const r = (state.ranges ?? []).find((x) => String(x?.id) === String(id));
    if (!r || String((r as any).kind ?? "") !== "selection") return null;

    const s0 = clamp(
      Math.min(Number((r as any).startSec ?? 0), Number((r as any).endSec ?? 0)),
      0,
      state.durationSec,
    );
    const e0 = clamp(
      Math.max(Number((r as any).startSec ?? 0), Number((r as any).endSec ?? 0)),
      0,
      state.durationSec,
    );
    if (!finiteNum(s0) || !finiteNum(e0) || e0 - s0 < 0.05) return null;

    return { id: String((r as any).id), startSec: s0, endSec: e0 };
  }, [state.durationSec, state.ranges, (state as any).selectedRangeId]);

  const lastSelection = useMemo(() => {
    const sels = (state.ranges ?? []).filter(
      (r) => String((r as any)?.kind ?? "") === "selection",
    );
    if (!sels.length) return null;

    const r = sels[sels.length - 1] as any;
    const s0 = clamp(
      Math.min(Number(r?.startSec ?? 0), Number(r?.endSec ?? 0)),
      0,
      state.durationSec,
    );
    const e0 = clamp(
      Math.max(Number(r?.startSec ?? 0), Number(r?.endSec ?? 0)),
      0,
      state.durationSec,
    );
    if (!finiteNum(s0) || !finiteNum(e0) || e0 - s0 < 0.05) return null;

    return { id: String(r.id), startSec: s0, endSec: e0 };
  }, [state.ranges, state.durationSec]);

  const loopEnabled = !!(state as any).loopSelectionEnabled;
  const loopRange = loopEnabled ? armedSelection : null;

  /* ----------------------------------------------------- */
  /* Canonical setters                                     */
  /* ----------------------------------------------------- */

  const setPlayhead = useCallback(
    (t: number, source: string, transient = false) => {
      dispatch({
        type: "timeline/setPlayhead",
        payload: { timeSec: clamp(t, 0, state.durationSec) },
        meta: { source, transient },
      } as any);
    },
    [dispatch, state.durationSec],
  );

  /* ----------------------------------------------------- */
  /* NPC telemetry → envelope suggestions                  */
  /* ----------------------------------------------------- */

  useEffect(() => {
    const suggestions = buildNpcEnvelopeSuggestions({
      markers: state.markers ?? [],
      durationSec: state.durationSec,
    });

    if (!suggestions.length) return;
    emitNpcEnvelopeSuggestions(suggestions);
  }, [state.markers, state.ranges, state.durationSec]);

  /* ----------------------------------------------------- */
  /* Actions                                               */
  /* ----------------------------------------------------- */

  const addMarker = useCallback(
    (opts?: { npc?: boolean }) => {
      const timeSec = snapTime(state.playheadSec, {
        enabled: snap.enabled,
        mode: snap.mode,
        fps: snap.fps,
        bpm: snap.bpm,
        beatDiv: snap.beatDiv,
        markers: state.markers,
      });

      const npc = !!opts?.npc;
      const fps = Number(snap.fps ?? 30) || 30;

      const marker = {
        id: makeId(),
        timeSec,
        label: npc ? "npc:hit" : embeddedInOmniStudio ? "Cue Anchor" : "Marker",
        type: npc ? ("npc" as const) : ("cue" as const),
        origin: "transport_panel" as const,
        note: npc ? "NPC cue" : "Manual marker",
        description: npc ? "NPC cue" : "Manual marker",
        color: npc ? NPC_MARKER_DEFAULT_COLOR : MANUAL_MARKER_DEFAULT_COLOR,
        frameIndex: markerFrameIndex(timeSec, fps),
        visible: true,
        source: "user" as const,
      };

      dispatch({ type: "marker/add", payload: { marker }, meta: { source: "user" } } as any);
      emitMarkerAdded({ ...marker });
    },
    [dispatch, state.playheadSec, snap.enabled, snap.mode, snap.fps, state.markers],
  );

  const toggleSnap = useCallback(() => {
    dispatch({
      type: "timeline/setSnap",
      payload: { enabled: !snap.enabled },
      meta: { source: "user" },
    } as any);
  }, [dispatch, snap.enabled]);

  const cycleSnap = useCallback(() => {
    dispatch({
      type: "timeline/setSnap",
      payload: { mode: cycleSnapMode((snap.mode as SnapMode | undefined) ?? "second") },
      meta: { source: "user" },
    } as any);
  }, [dispatch, snap.mode]);

  /* -------------------------------------------------------------- */
  /* Zoom controls                                                  */
  /* -------------------------------------------------------------- */

  const zoomIn = useCallback(() => {
    dispatch({
      type: "timeline/setZoom",
      payload: { pxPerSec: clamp(pxPerSec * 1.1, 20, 400) },
      meta: { source: "user" },
    } as any);
  }, [dispatch, pxPerSec]);

  const zoomOut = useCallback(() => {
    dispatch({
      type: "timeline/setZoom",
      payload: { pxPerSec: clamp(pxPerSec / 1.1, 20, 400) },
      meta: { source: "user" },
    } as any);
  }, [dispatch, pxPerSec]);

  const prev = useCallback(() => {
    if (prevMarker == null) return;
    setPlayhead(prevMarker, "transport/prev");
  }, [prevMarker, setPlayhead]);

  const next = useCallback(() => {
    if (nextMarker == null) return;
    setPlayhead(nextMarker, "transport/next");
  }, [nextMarker, setPlayhead]);

  const toggleLoop = useCallback(() => {
    const enabling = !loopEnabled;

    // If enabling and nothing armed, arm last selection to keep UX smooth.
    if (enabling && !armedSelection && lastSelection) {
      dispatch({
        type: "range/select",
        payload: { rangeId: lastSelection.id },
        meta: { source: "transport/loop-arm" },
      } as any);
    }

    dispatch({
      type: "timeline/setLoopSelection",
      payload: { enabled: enabling },
      meta: { source: "user" },
    } as any);
  }, [dispatch, loopEnabled, armedSelection, lastSelection]);

  const toggleAI = useCallback(() => {
    setAiEnabled((v) => {
      const nextVal = !v;
      window.dispatchEvent(
        new CustomEvent("omnicine:ai:toggle", { detail: { enabled: nextVal } }),
      );
      return nextVal;
    });
  }, []);

  const exportTimeline = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent("omnicine:export", { detail: { kind: "timeline", format: "json" } }),
    );
  }, []);

  const openHelp = useCallback(() => {
    emitTimelineHelpOpen();
  }, []);

  const addPhaseRanges = useCallback(() => {
    emitAddPhaseRanges();
  }, []);

  const laneFocusMode = String((state as any)?.laneFocus?.mode ?? "all").trim().toLowerCase() === "import"
    ? "import"
    : "all";
  const hasStoredImportedLaneFocus = !!lastImportedLaneFocusRef.current;

  const toggleLaneFocusMode = useCallback(() => {
    if (laneFocusMode === "import") {
      dispatch({
        type: "timeline/setLaneFocus",
        payload: { mode: "all" },
        meta: { source: "user", note: "transport/show all lanes" },
      } as any);
      return;
    }

    const snapshot = lastImportedLaneFocusRef.current;
    if (!snapshot) return;

    dispatch({
      type: "timeline/setLaneFocus",
      payload: snapshot,
      meta: { source: "user", note: "transport/restore imported lanes" },
    } as any);
  }, [dispatch, laneFocusMode]);

  /* ----------------------------------------------------- */
  /* Deterministic play runner                              */
  /* ----------------------------------------------------- */

  const [playing, setPlaying] = useState(false);

  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  const playheadRef = useRef<number>(finiteNum(state.playheadSec) ? state.playheadSec : 0);
  const durationRef = useRef<number>(finiteNum(state.durationSec) ? state.durationSec : 0);
  const loopRef = useRef<{ startSec: number; endSec: number } | null>(loopRange);

  useEffect(() => {
    playheadRef.current = finiteNum(state.playheadSec) ? state.playheadSec : 0;
  }, [state.playheadSec]);

  useEffect(() => {
    durationRef.current = finiteNum(state.durationSec) ? state.durationSec : 0;
  }, [state.durationSec]);

  useEffect(() => {
    loopRef.current = loopRange;
  }, [loopRange]);

  const STEP_FPS = 30;
  const STEP_SEC = 1 / STEP_FPS;
  const MAX_DT = 0.25;

  const togglePlay = useCallback(() => setPlaying((v) => !v), []);

  useEffect(() => {
    emitTransportPlaying({ enabled: playing });
  }, [playing]);

  useEffect(() => {
    if (!playing) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
      return;
    }

    // On start: if loop is armed and playhead is outside, jump to loop start
    const lr0 = loopRef.current;
    if (lr0) {
      const p0 = playheadRef.current;
      if (p0 < lr0.startSec || p0 > lr0.endSec) {
        setPlayhead(lr0.startSec, "transport/loop-jump");
        playheadRef.current = lr0.startSec;
      }
    }

    const tick = (ts: number) => {
      const last = lastTsRef.current;
      lastTsRef.current = ts;

      if (last == null) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      let dt = (ts - last) / 1000;
      dt = clamp(dt, 0, MAX_DT);

      let steps = Math.floor(dt / STEP_SEC);
      if (steps < 1) steps = 1;
      if (steps > 10) steps = 10;

      let t = playheadRef.current;
      const D = Math.max(0, durationRef.current);
      const lr = loopRef.current;

      for (let i = 0; i < steps; i++) {
        t += STEP_SEC;

        if (lr) {
          if (t >= lr.endSec) t = lr.startSec;
          t = clamp(t, lr.startSec, lr.endSec);
        } else {
          if (t >= D) {
            t = D;
            setPlayhead(t, "transport/end");
            playheadRef.current = t;
            setPlaying(false);
            return;
          }
        }
      }

      playheadRef.current = t;
      setPlayhead(t, "transport/play", true);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTsRef.current = null;
    };
  }, [playing, setPlayhead]);

  /* ----------------------------------------------------- */
  /* Render                                                 */
  /* ----------------------------------------------------- */

  const playLabel = playing ? "Pause" : "Play";
  const authorActionTone = embeddedInOmniStudio ? V_NEUTRAL : V_AUTHOR;
  const npcActionTone = embeddedInOmniStudio ? V_NEUTRAL : V_NPC;
  const exportActionTone = embeddedInOmniStudio ? V_NEUTRAL : V_EXPORT;
  const helpActionTone = embeddedInOmniStudio ? V_NEUTRAL : V_HELP;
  const phaseLabel = embeddedInOmniStudio ? "Phase Read" : "Add Phase Ranges";
  const markerLabel = embeddedInOmniStudio ? "Cue Anchor" : "Add Marker";
  const npcLabel = embeddedInOmniStudio ? "NPC Echo" : "Add NPC";
  const snapModeLabel = formatSnapModeLabel((snap.mode as SnapMode | undefined) ?? "second");
  const snapLabel = embeddedInOmniStudio ? `Snap ${snap.enabled ? "On" : "Off"}` : `Snap: ${snap.enabled ? "On" : "Off"}`;
  const snapDetailLabel =
    snapModeLabel === "Frame"
      ? `${snapModeLabel} · ${Math.round(snap.fps || 30)} fps`
      : snapModeLabel === "Beat"
        ? `${snapModeLabel} · ${Math.round(snap.bpm || 120)} bpm`
        : snapModeLabel;
  const pxPerFrame = pxPerSec / Math.max(1, snap.fps || 30);
  const scaleLabel = embeddedInOmniStudio
    ? `Scale ${Math.round(pxPerSec)} px/s · ${pxPerFrame.toFixed(1)} px/fr`
    : `${Math.round(pxPerSec)} px/s · ${pxPerFrame.toFixed(1)} px/fr`;
  const exportLabel = embeddedInOmniStudio ? "Export View" : "Export";
  const importLabel = embeddedInOmniStudio ? "Ingest View" : "Import";
  const importBusyLabel = embeddedInOmniStudio ? "Ingesting…" : "Importing…";
  const helpLabel = embeddedInOmniStudio ? "Guide" : "Help";
  const laneFocusLabel = laneFocusMode === "import"
    ? embeddedInOmniStudio
      ? "Show Full Deck"
      : "Show All Lanes"
    : embeddedInOmniStudio
      ? "Show Import Deck"
      : "Show Import Lanes";

  return (
    <div
      data-oc-transport-action-rail="1"
      className="flex items-center justify-between gap-3 rounded-[20px] border border-white/10 px-3 py-1.5 text-white/92"
      style={{
        background: "linear-gradient(180deg, rgba(255,255,255,0.045), rgba(255,255,255,0.025))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      {/* Left block (Transport / playback) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={togglePlay}
          className={cx(DOCK_BTN_BASE, "min-w-[88px]", V_NEUTRAL, playing && "bg-[rgba(255,255,255,0.12)]")}
        >
          {playLabel}
        </button>

        <button
          type="button"
          onClick={prev}
          disabled={prevMarker == null}
          className={cx(DOCK_BTN_BASE, V_NEUTRAL, prevMarker == null && DOCK_BTN_DISABLED)}
        >
          Prev
        </button>

        <button
          type="button"
          onClick={next}
          disabled={nextMarker == null}
          className={cx(DOCK_BTN_BASE, V_NEUTRAL, nextMarker == null && DOCK_BTN_DISABLED)}
        >
          Next
        </button>

        <div className="mx-2 h-6 w-px bg-white/10" />

        <div className="flex items-baseline gap-2">
          <div className="text-[11px] text-white/70">Playhead</div>
          <div className="font-mono text-[12px]">{formatTime(state.playheadSec)}</div>
          <div className="text-[11px] text-white/50">({state.playheadSec.toFixed(2)}s)</div>
        </div>

        <button
          type="button"
          onClick={toggleLoop}
          className={cx(DOCK_BTN_BASE, "min-w-[104px]", loopEnabled ? V_AUTHOR : V_NEUTRAL)}
          title="Loop the armed selection range (click a selection chip to arm it)"
        >
          <StatusDot on={loopEnabled} tone="cyan" />
          <span>Loop:</span>
          <span className="font-medium">{loopEnabled ? "On" : "Off"}</span>
        </button>

        {loopEnabled && (
          <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-[11px] text-white/70">
            {armedSelection
              ? `${armedSelection.startSec.toFixed(2)}–${armedSelection.endSec.toFixed(2)}s`
              : "No selection armed"}
          </div>
        )}
      </div>

      {/* Right block: dock actions — semantic pills */}
      <div className="flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          onClick={addPhaseRanges}
          className={cx(DOCK_BTN_BASE, "min-w-[120px]", authorActionTone)}
          title={embeddedInOmniStudio ? "Surface the four phase reads across the consequence deck" : "Create/repair the 4 narrative phase ranges across the full duration"}
        >
          {phaseLabel}
        </button>

        <button
          type="button"
          onClick={(e) => addMarker({ npc: (e as any).shiftKey })}
          className={cx(DOCK_BTN_BASE, authorActionTone)}
          title={embeddedInOmniStudio ? 'Place a cue anchor. Hold Shift to emit "npc:hit".' : 'Add Marker. Hold Shift to add "npc:hit".'}
        >
          {markerLabel}
        </button>

        <button
          type="button"
          onClick={() => addMarker({ npc: true })}
          className={cx(DOCK_BTN_BASE, npcActionTone)}
          title={embeddedInOmniStudio ? 'Emit the NPC echo marker: "npc:hit"' : 'Add NPC marker: "npc:hit"'}
        >
          {npcLabel}
        </button>

        <button
          type="button"
          onClick={toggleSnap}
          className={cx(DOCK_BTN_BASE, "min-w-[104px]", snap.enabled ? V_TOGGLE_ON : V_NEUTRAL)}
          title={embeddedInOmniStudio ? "Snap constrains preview placement to the current snap mode" : "Snap quantizes edits to your snap mode"}
        >
          <StatusDot on={!!snap.enabled} tone="amber" />
          {snapLabel}
        </button>

        <button
          type="button"
          onClick={cycleSnap}
          className={cx(DOCK_BTN_BASE, "min-w-[146px]", snap.enabled ? V_TOGGLE_ON : V_NEUTRAL)}
          title={embeddedInOmniStudio ? "Cycle the cue snap resolution" : "Cycle snap mode between frame, second, marker, and beat"}
        >
          {snapDetailLabel}
        </button>

        <button
          type="button"
          onClick={zoomOut}
          className={cx(DOCK_BTN_BASE, "min-w-[64px]", V_NEUTRAL)}
          aria-label="Zoom out"
        >
          −
        </button>

        <div className="h-9 min-w-[126px] px-3 rounded-xl border border-white/12 bg-[rgba(255,255,255,0.06)] inline-flex items-center justify-center font-mono text-[11px] text-white/70">
          {scaleLabel}
        </div>

        <button
          type="button"
          onClick={zoomIn}
          className={cx(DOCK_BTN_BASE, "min-w-[64px]", V_NEUTRAL)}
          aria-label="Zoom in"
        >
          +
        </button>

        <button
          type="button"
          onClick={toggleAI}
          className={cx(DOCK_BTN_BASE, "min-w-[96px]", aiEnabled ? V_NPC : V_NEUTRAL)}
          title={embeddedInOmniStudio ? "AI read toggle (event only for now)" : "AI toggle (event only for now)"}
        >
          <StatusDot on={aiEnabled} tone="violet" />
          AI: {aiEnabled ? "On" : "Off"}
        </button>

        <button
          type="button"
          onClick={exportTimeline}
          className={cx(DOCK_BTN_BASE, exportActionTone)}
          title={embeddedInOmniStudio ? "Export the current read deck view" : "Export timeline (event only for now)"}
        >
          <StatusDot on={true} tone="emerald" />
          {exportLabel}
        </button>

        <ImportButton
          className={cx(DOCK_BTN_BASE, exportActionTone)}
          label={importLabel}
          busyLabel={importBusyLabel}
          title={embeddedInOmniStudio ? "Ingest a media view into the downstream deck" : undefined}
        />

        <button
          type="button"
          onClick={toggleLaneFocusMode}
          disabled={laneFocusMode !== "import" && !hasStoredImportedLaneFocus}
          className={cx(
            DOCK_BTN_BASE,
            "min-w-[138px]",
            laneFocusMode === "import" ? authorActionTone : V_NEUTRAL,
            laneFocusMode !== "import" && !hasStoredImportedLaneFocus && DOCK_BTN_DISABLED,
          )}
          title={
            laneFocusMode === "import"
              ? embeddedInOmniStudio
                ? "Return to the full deck after an import-focused view"
                : "Restore the full timeline lane set"
              : hasStoredImportedLaneFocus
                ? embeddedInOmniStudio
                  ? "Return to the last imported lane deck"
                  : "Return to the most recent import-focused lane set"
                : embeddedInOmniStudio
                  ? "Import media first to focus the deck"
                  : "Import media first to focus specific lanes"
          }
        >
          <StatusDot on={laneFocusMode === "import"} tone="cyan" />
          {laneFocusLabel}
        </button>

        <div className="h-9 inline-flex items-center">
          <PodcastExportPanel placement="inline" />
        </div>

        <button
          type="button"
          onClick={openHelp}
          className={cx(DOCK_BTN_BASE, helpActionTone)}
          title={embeddedInOmniStudio ? "Projection guide" : "Timeline Help"}
        >
          {helpLabel}
        </button>
      </div>
    </div>
  );
}
